import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { MessageCircle, X, Loader2, RotateCw } from 'lucide-react';
import { getAccessToken } from '../api';
import { getSupportConfig, getSupportIdentity, SupportConfig, SupportIdentity } from '../api/support';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { supportText } from '../locales/support';
import './SupportChat.css';

type Connection = { config: SupportConfig; identity: SupportIdentity | null; session: string; generation: number };

// Used only to invalidate the UI. The identity endpoint and Chatwoot HMAC remain
// the authorities. Token refresh for the same account must not close its chat.
function sessionKey(): string {
  const token = getAccessToken();
  if (!token) return 'guest';
  try {
    const subject = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).sub;
    if (typeof subject === 'string') return `user:${subject}`;
  } catch { /* An opaque token still requires authenticated identity. */ }
  return `authenticated:${token}`;
}

// Avoid putting lot IDs, account paths, search terms or auth/payment query
// parameters in the operator context. The embedded SDK sees a fixed URL.
export function supportPage(pathname: string): string {
  const section = pathname.split('/')[1];
  return ['', 'lots', 'suppliers', 'calculator', 'financial-planning', 'tracker', 'profile',
    'plans', 'login', 'register', 'forgot-password', 'contacts', 'questions', 'services'].includes(section)
    ? `/${section}` : '/';
}

export default function SupportChat() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const { pathname } = useLocation();
  const text = supportText[language];
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [availability, setAvailability] = useState<'loading' | 'enabled' | 'disabled' | 'unavailable'>('loading');
  const [connection, setConnection] = useState<Connection | null>(null);
  const [unread, setUnread] = useState(0);
  const [storageRevision, setStorageRevision] = useState(0);
  const [launcherBottom, setLauncherBottom] = useState(0);
  const iframe = useRef<HTMLIFrameElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const launcher = useRef<HTMLButtonElement>(null);
  const opener = useRef<HTMLElement | null>(null);
  const generation = useRef(0);
  const abort = useRef<AbortController | null>(null);
  const cachedConfig = useRef<SupportConfig | null>(null);
  const pendingConfig = useRef<Promise<SupportConfig> | null>(null);
  const lastSession = useRef(sessionKey());
  const latest = useRef({ open, language, pathname, session: sessionKey() });
  latest.current = { open, language, pathname, session: sessionKey() };
  const prerender = Boolean(window.__GL_PRERENDER__);
  const hidden = pathname.startsWith('/admin') || prerender;

  const requestConfig = useCallback((signal: AbortSignal) => {
    const request = getSupportConfig(signal).then(config => {
      if (!signal.aborted) {
        cachedConfig.current = config;
        setAvailability(config.enabled ? 'enabled' : 'disabled');
      }
      return config;
    }).catch((error: unknown) => {
      if (!signal.aborted) {
        const failure = error as { response?: { status?: number }; code?: string };
        setAvailability(failure.response?.status === 404 || failure.code === 'SUPPORT_CONFIG_INVALID' ? 'disabled' : 'unavailable');
      }
      throw error;
    }).finally(() => { if (pendingConfig.current === request) pendingConfig.current = null; });
    pendingConfig.current = request;
    return request;
  }, []);

  useEffect(() => {
    if (window.__GL_PRERENDER__) return;
    // Only the public feature flag is read eagerly. No visitor, cookies or
    // Chatwoot connection is created before the user deliberately opens chat.
    const controller = new AbortController();
    void requestConfig(controller.signal).catch(() => {});
    return () => controller.abort();
  }, [requestConfig]);

  const close = useCallback(() => {
    setOpen(false);
    window.requestAnimationFrame(() => {
      if (opener.current?.isConnected) opener.current.focus();
      else launcher.current?.focus();
    });
  }, []);

  const initialize = useCallback(async () => {
    if (window.__GL_PRERENDER__) return;
    abort.current?.abort();
    const controller = new AbortController();
    abort.current = controller;
    const currentGeneration = ++generation.current;
    const session = sessionKey();
    setConnection(null); setPhase('loading'); setUnread(0);
    try {
      const config = cachedConfig.current || await (pendingConfig.current || requestConfig(controller.signal));
      if (!config.enabled) throw new Error('Support disabled');
      // A signed-in visitor must never silently fall back to an anonymous chat.
      const identity = session === 'guest' ? null : await getSupportIdentity(controller.signal);
      if (controller.signal.aborted || currentGeneration !== generation.current || sessionKey() !== session) return;
      setConnection({ config, identity, session, generation: currentGeneration });
    } catch {
      if (!controller.signal.aborted && currentGeneration === generation.current) setPhase('error');
    }
  }, [requestConfig]);

  const show = useCallback(() => {
    if (window.__GL_PRERENDER__) return;
    opener.current = document.activeElement as HTMLElement;
    setOpen(true); setUnread(0);
    if (phase === 'idle' || phase === 'error') void initialize();
  }, [phase, initialize]);

  useEffect(() => {
    const externalOpen = () => show();
    const storage = (event: StorageEvent) => { if (!event.key || event.key === 'access_token') setStorageRevision(v => v + 1); };
    window.addEventListener('gl:open-support', externalOpen);
    window.addEventListener('storage', storage);
    return () => {
      window.removeEventListener('gl:open-support', externalOpen);
      window.removeEventListener('storage', storage);
    };
  }, [show]);

  useLayoutEffect(() => {
    // Destroy the iframe before repaint on logout/account switch: SDK cookies,
    // queued events and sockets from one account cannot revive another's UI.
    const session = sessionKey();
    if (lastSession.current !== session) {
      lastSession.current = session;
      generation.current++; abort.current?.abort();
      setConnection(null); setPhase('idle'); setOpen(false); setUnread(0);
    }
    // Only account changes trigger this effect, not connection progress.
  }, [user?.email, storageRevision]);

  useEffect(() => () => { generation.current++; abort.current?.abort(); }, []);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('gl:support-visibility', { detail: open && !hidden }));
    return () => { window.dispatchEvent(new CustomEvent('gl:support-visibility', { detail: false })); };
  }, [open, hidden]);

  useEffect(() => {
    if (prerender) return;
    let observed: Element | null = null;
    let previousBottom = 0;
    const update = () => {
      const banner = document.querySelector('[data-gl-cookie-banner]');
      if (banner !== observed) {
        resize?.disconnect(); observed = banner;
        if (banner) resize?.observe(banner);
      }
      const rect = banner?.getBoundingClientRect();
      const bottom = rect && rect.height > 0 && rect.right > window.innerWidth - 240
        ? Math.max(20, window.innerHeight - rect.top + 12) : 0;
      if (bottom !== previousBottom) { previousBottom = bottom; setLauncherBottom(bottom); }
    };
    const resize = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    const mutation = new MutationObserver(update);
    mutation.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('resize', update); update();
    return () => { resize?.disconnect(); mutation.disconnect(); window.removeEventListener('resize', update); };
  }, [prerender]);

  useEffect(() => {
    if (!connection) return;
    const send = (type: string, payload = {}) => iframe.current?.contentWindow?.postMessage(
      { channel: 'glauncher-support', type, ...payload }, window.location.origin);
    const timeout = window.setTimeout(() => { setPhase('error'); setConnection(null); }, 30000);
    const receive = (event: MessageEvent) => {
      if (event.origin !== window.location.origin || event.source !== iframe.current?.contentWindow
          || event.data?.channel !== 'glauncher-support' || connection.session !== sessionKey()) return;
      if (event.data.type === 'boot') send('init', {
        config: connection.config, identity: connection.identity, locale: latest.current.language,
        page: supportPage(latest.current.pathname), open: latest.current.open,
      });
      if (event.data.type === 'ready') { window.clearTimeout(timeout); setPhase('ready'); }
      if (event.data.type === 'error') { window.clearTimeout(timeout); setConnection(null); setPhase('error'); }
      if (event.data.type === 'close') close();
      if (event.data.type === 'unread' && !latest.current.open) setUnread(v => Math.min(v + 1, 99));
    };
    window.addEventListener('message', receive);
    return () => { window.clearTimeout(timeout); window.removeEventListener('message', receive); };
  }, [connection, close]);

  useEffect(() => {
    iframe.current?.contentWindow?.postMessage({ channel: 'glauncher-support', type: 'update',
      open: open && !hidden, locale: language, page: supportPage(pathname) }, window.location.origin);
  }, [open, hidden, language, pathname, phase]);

  useEffect(() => {
    if (!open || hidden) return;
    closeButton.current?.focus();
    const escape = (event: KeyboardEvent) => { if (event.key === 'Escape') { event.stopPropagation(); close(); } };
    document.addEventListener('keydown', escape);
    return () => document.removeEventListener('keydown', escape);
  }, [open, hidden, close]);

  if (prerender) return null;
  return createPortal(<div className="gl-support" data-testid="support-widget">
    {!hidden && !open && (availability === 'enabled' || availability === 'unavailable') && <button ref={launcher} type="button" className="gl-support-launcher" onClick={show}
      style={launcherBottom ? { bottom: launcherBottom } : undefined}
      aria-label={text.open} aria-haspopup="dialog" aria-expanded={false}>
      <MessageCircle className="h-5 w-5" aria-hidden="true" /><span>{text.open}</span>
      {unread > 0 && <span className="gl-support-badge" aria-label={text.unread}>{unread}</span>}
    </button>}
    <section className="gl-support-panel" role="dialog" aria-label={text.title}
      hidden={!open || hidden} data-testid="support-panel">
      <header className="gl-support-header">
        <div><strong>{text.title}</strong><p>{text.privacy}<br />{text.files}</p></div>
        <button type="button" ref={closeButton} onClick={close} aria-label={text.close}><X className="h-5 w-5" /></button>
      </header>
      <div className="gl-support-content">
        {phase === 'loading' && <div className="gl-support-status" role="status"><Loader2 className="h-6 w-6 animate-spin" /><p>{text.loading}</p></div>}
        {phase === 'error' && <div className="gl-support-status" role="alert">
          <p className="font-semibold text-slate-900">{text.unavailable}</p><p>{text.fallback}</p>
          <button type="button" className="gl-support-retry" onClick={() => void initialize()}><RotateCw className="h-4 w-4" />{text.retry}</button>
          <Link to="/contacts" onClick={close} className="font-semibold text-goszakup-700 underline underline-offset-4">{text.contacts}</Link>
        </div>}
        {connection && <iframe key={connection.generation} ref={iframe} title={text.frame}
          src="/support-frame.html" referrerPolicy="no-referrer"
          className={phase === 'ready' ? 'gl-support-frame' : 'gl-support-frame gl-support-frame-loading'}
          onLoad={() => iframe.current?.contentWindow?.postMessage({ channel: 'glauncher-support', type: 'ping' }, window.location.origin)} />}
      </div>
    </section>
  </div>, document.body);
}
